import { css } from 'styled-components';
import { betweenScreen, fromScreen, toScreen } from '@doltech/core/lib/responsive/responsive.util';

export const innerBorder = (
  style: string,
  position?: 'all' | 'top' | 'left' | 'bottom' | 'right',
  inheritColor = false
) => {
  const borderProperty = position && position !== 'all' ? `border-${position}` : 'border';

  return css`
    position: relative;

    &:after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      border-radius: inherit;
      ${borderProperty}: ${style || 'none'};
      ${inheritColor ? 'border-color: inherit;' : undefined}
      pointer-events: none;
      user-select: none;
    }
  `;
};

/* stylelint-disable */
export const dashedBorderStyles = ({
  size = 1,
  color = '#313A44',
  width = 5,
  gap = 5,
  borderRadius = 0,
}: {
  size?: number;
  color?: string;
  width?: number;
  gap?: number;
  borderRadius?: number | string;
}) => {
  return css`
    background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='${borderRadius}' ry='${borderRadius}' stroke='${encodeURIComponent(
      color
    )}' stroke-width='${size *
    2}' stroke-dasharray='${width} ${gap}' stroke-dashoffset='0' stroke-linecap='butt'/%3e%3c/svg%3e");
    background-position: center;
  `;
};

export const dashedBorder = ({
  size = 1,
  color = '#313A44',
  width = 5,
  gap = 5,
  borderRadius: borderRadiusValue = 0,
}: {
  size?: number;
  color?: string;
  width?: number;
  gap?: number;
  borderRadius?: number | string;
}) => {
  const borderRadius =
    typeof borderRadiusValue === 'number' ? `${borderRadiusValue}px` : borderRadiusValue;

  return css`
    position: relative;
    z-index: 0;

    &:after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      border-radius: ${borderRadius};
      z-index: -1;
      ${dashedBorderStyles({
        size,
        color,
        width,
        gap,
        borderRadius,
      })}
    }
  `;
};

export const horizontalSpace = (spaceValue: number | string = 8) => {
  const space = typeof spaceValue === 'number' ? `${spaceValue}px` : spaceValue;

  return css`
    > * {
      &:not(:last-child) {
        margin-right: ${space} !important;
      }
    }
  `;
};

export const verticalSpace = (spaceValue: number | string = 8) => {
  const space = typeof spaceValue === 'number' ? `${spaceValue}px` : spaceValue;

  return css`
    > * {
      &:not(:last-child) {
        margin-bottom: ${space} !important;
      }
    }
  `;
};

export const horizontalSpaceLineDivider = ({
  space: spaceValue = 8,
  lineSize = 1,
  lineLength,
  lineColor = '#D2D8DF',
}: {
  space?: string | number;
  lineSize?: number;
  lineLength?: number;
  lineColor?: string;
}) => {
  const space = typeof spaceValue === 'number' ? `${spaceValue}px` : spaceValue;
  const verticalPosition = lineLength ? `calc(50% - ${lineLength / 2}px)` : 0;

  return css`
    > * {
      &:not(:last-child) {
        position: relative;
        margin-right: calc(2 * ${space}) !important;

        &:after {
          content: '';
          position: absolute;
          top: ${verticalPosition};
          bottom: ${verticalPosition};
          right: -${space};
          border-right: ${lineSize}px solid ${lineColor};
        }
      }
    }
  `;
};

export const verticalSpaceLineDivider = (
  configs:
    | {
        space?: string | number;
        lineSize?: number;
        lineColor?: string;
      }
    | false
) => {
  if (!configs) {
    return css`
      > * {
        &:not(:last-child) {
          position: relative;
          margin-bottom: 0;

          &:after {
            content: none;
          }
        }
      }
    `;
  }

  const { space: spaceValue = 8, lineSize = 1, lineColor = '#D2D8DF' } = configs;
  const space = typeof spaceValue === 'number' ? `${spaceValue}px` : spaceValue;

  return css`
    > * {
      &:not(:last-child) {
        position: relative;
        margin-bottom: calc(2 * ${space}) !important;

        &:after {
          content: '';
          position: absolute;
          left: 0;
          right: 0;
          bottom: -${space};
          border-bottom: ${lineSize}px solid ${lineColor};
        }
      }
    }
  `;
};

export const horizontalSpaceDotDivider = ({
  space: spaceValue = 8,
  dotSize = 3,
  dotColor = '#D2D8DF',
}: {
  space?: string | number;
  dotSize?: number;
  dotColor?: string;
}) => {
  const space = typeof spaceValue === 'number' ? `${spaceValue}px` : spaceValue;

  return css`
    > * {
      &:not(:last-child) {
        position: relative;
        margin-right: calc(2 * ${space}) !important;

        &:after {
          content: '';
          position: absolute;
          top: calc(50% - 3px / 2);
          left: calc(100% + ${space} - 3px / 2);
          width: ${dotSize}px;
          height: ${dotSize}px;
          background-color: ${dotColor};
          border-radius: 50%;
        }
      }
    }
  `;
};

type RowGap = number | string;
type ColumGap = number | string;

export const flexGap = (
  gapValue: number | string | [RowGap, ColumGap] = 8,
  flexDirection: 'row' | 'column' = 'row'
) => {
  if (Array.isArray(gapValue)) {
    const rowGap = typeof gapValue[0] === 'number' ? `${gapValue[0]}px` : gapValue[0];
    const columnGap = typeof gapValue[1] === 'number' ? `${gapValue[1]}px` : gapValue[1];

    return css`
      margin-bottom: -${columnGap};
      margin-right: -${rowGap};

      > * {
        margin-bottom: ${columnGap} !important;
        margin-right: ${rowGap} !important;
      }
    `;
  }

  const gap = typeof gapValue === 'number' ? `${gapValue}px` : gapValue;

  if (flexDirection === 'column') {
    return verticalSpace(gap);
  }

  return horizontalSpace(gap);
};

export const scrollBarStyles = (
  styles: {
    size?: number;
    trackColor?: string;
    thumbColor?: string;
    thumbSize?: number;
  } = {}
) => {
  const { size = 12, trackColor = '#FFFFFF', thumbColor = '#D2D8DF', thumbSize = 4 } = styles;

  return css`
    &::-webkit-scrollbar {
      width: ${size}px;
      height: ${size}px;
    }
    &::-webkit-scrollbar-track {
      background-color: ${trackColor};
    }
    &::-webkit-scrollbar-thumb {
      background-color: ${thumbColor};
      border: ${(size - thumbSize) / 2}px solid ${trackColor};
    }
  `;
};

export const hideScrollBar = () => {
  return css`
    &::-webkit-scrollbar {
      display: none;
    }
  `;
};

export const dynamicScrollBarStyles = () => {
  return css`
    &::-webkit-scrollbar {
      display: none;
    }

    &:hover {
      &::-webkit-scrollbar {
        display: initial;
      }
    }
  `;
};

export const scrollBarStylesGeneral = (thumbColor = '#D2D8DF') => {
  return scrollBarStyles({
    size: 12,
    trackColor: '#FFFFFF',
    thumbColor,
    thumbSize: 4,
  });
};

export const scrollBarStylesCompact = (thumbColor = '#F2F4F7') => {
  return scrollBarStyles({
    size: 10,
    trackColor: '#FFFFFF',
    thumbColor,
    thumbSize: 2,
  });
};

export const scrollbarStylesHidden = () => {
  return css`
    &::-webkit-scrollbar,
    &::-webkit-scrollbar-track,
    &::-webkit-scrollbar-thumb {
      display: none;
    }
  `;
};

// This utility should be used only when you have not used betweenScreen before in the same element
export const showBetweenScreen = (fromSize = 375, toSize = 768) => {
  return css`
    display: none;
    ${betweenScreen(fromSize, toSize)} {
      display: flex;
    }
  `;
};

// This utility should be used only when you have not used fromScreen before in the same element
export const showFromScreen = (fromSize = 375) => {
  return css`
    display: none;
    ${fromScreen(fromSize)} {
      display: flex;
    }
  `;
};

// This utility should be used only when you have not used toScreen before in the same element
export const showToScreen = (toSize = 768) => {
  return css`
    display: none;
    ${toScreen(toSize)} {
      display: flex;
    }
  `;
};

export const borderDashed = (color = '#E3E7ED') => {
  return css`
    background-image: linear-gradient(90deg, ${color} 50%, transparent 50%);
    background-size: 15px 1px; // create new props for size in case you want to change it
    background-repeat: repeat-x;
  `;
};
